import { useState, useEffect, useMemo, useCallback } from "react";
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { useAccount, useReadContract, useWaitForTransactionReceipt } from 'wagmi';
import { AppSidebar } from "@/components/app-sidebar";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/breadcrumb";
import { Separator } from "@/components/separator";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/sidebar";
import { Link } from "react-router-dom";
import { Button } from "@/components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/tabs";
import { Input } from "@/components/input";
import {
    RiNodeTree,
    RiShareLine,
    RiFileCopyLine,
    RiAddLine,
    RiWallet3Line,
    RiLoader4Line,
    RiArrowRightUpLine,
    RiRefreshLine,
    RiStackLine,
} from "@remixicon/react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/table";
import { useNodeActionsV3 } from "@/hooks/use-node-actions-v3";
import { useLRSNodeV3 } from "@/providers/global-data.provider";
import { LRSNODE_V3_CONTRACT_CONFIG, type Node2MigrationStats } from "@/configs/lrsnode.config";
import BuyNodeModal from "@/components/buy-node-modal";
import CreateReferCodeModal from "@/components/create-refer-code-modal";

const V3_PLAN_STOCK_PLANS = [
  { id: 1, label: "Pro key" },
  { id: 2, label: "Lite key" },
] as const;

const PLAN_TABLE_LABEL: Record<string, string> = {
  "1": "Pro key",
  "2": "Lite key",
};

function V3PlanStockTile({
  planId,
  label,
  enabled,
}: {
  planId: number;
  label: string;
  enabled: boolean;
}) {
  const { data: planRaw, isLoading: isLoadingPlan } = useReadContract({
    ...LRSNODE_V3_CONTRACT_CONFIG,
    functionName: "plans",
    args: [BigInt(planId)],
    query: { enabled },
  });

  const { data: remainingRaw, isLoading: isLoadingRemaining } = useReadContract({
    ...LRSNODE_V3_CONTRACT_CONFIG,
    functionName: "planAvailable",
    args: [BigInt(planId)],
    query: { enabled },
  });

  const planMeta = useMemo(() => {
    if (!planRaw || !Array.isArray(planRaw)) return undefined;
    return {
      maxStock: planRaw[2] as bigint,
      soldStock: planRaw[3] as bigint,
      isActive: Boolean(planRaw[4]),
    };
  }, [planRaw]);

  const remaining = (remainingRaw as bigint | undefined) ?? 0n;
  const maxStock = planMeta?.maxStock ?? 0n;
  const soldStock = planMeta?.soldStock ?? 0n;
  const isActive = planMeta?.isActive ?? false;

  const maxN = Number(maxStock);
  const remN = Number(remaining);
  const pctRemaining = maxN > 0 && Number.isFinite(remN) ? Math.min(100, Math.max(0, (remN / maxN) * 100)) : 0;

  const stockTone =
    !isActive || maxN === 0
      ? "muted"
      : remN <= 0
        ? "soldOut"
        : maxN > 0 && remN / maxN < 0.1
          ? "low"
          : "ok";

  const shellClass =
    stockTone === "soldOut"
      ? "border-red-500/50 bg-red-500/5 ring-2 ring-red-500/20 shadow-sm shadow-red-500/10"
      : stockTone === "low"
        ? "border-amber-500/50 bg-amber-500/5 ring-2 ring-amber-500/15"
        : stockTone === "ok"
          ? "border-emerald-500/35 bg-emerald-500/5 ring-1 ring-emerald-500/20"
          : "border-border bg-background/30";

  const loading = isLoadingPlan || isLoadingRemaining;

  return (
    <div
      className={`rounded-xl border p-4 transition-all duration-200 ${shellClass}`}
      role="region"
      aria-label={`${label} remaining stock`}
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <div>
          <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Plan {planId}</div>
          <div className="text-base font-semibold text-foreground">{label}</div>
        </div>
        {!isActive && planMeta && (
          <span className="text-[10px] font-medium uppercase text-muted-foreground shrink-0">Inactive</span>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-6">
          <RiLoader4Line className="size-6 animate-spin text-[#FF8A3C]" aria-hidden />
        </div>
      ) : !planMeta ? (
        <p className="text-sm text-muted-foreground">Could not load plan.</p>
      ) : (
        <>
          <div className="flex items-baseline justify-between gap-2 mb-2">
            <span className="text-3xl font-bold tabular-nums text-[#FF8A3C]">{remaining.toString()}</span>
            <span className="text-xs text-muted-foreground tabular-nums">
              left of {maxStock.toString()}
            </span>
          </div>
          <p className="text-[11px] text-muted-foreground tabular-nums mb-2">
            Sold {soldStock.toString()} · Cap {maxStock.toString()}
          </p>
          {maxN > 0 && (
            <div
              className="h-2 w-full rounded-full bg-muted/80 overflow-hidden border border-border/60"
              role="progressbar"
              aria-valuenow={remN}
              aria-valuemin={0}
              aria-valuemax={maxN}
            >
              <div
                className={`h-full rounded-full transition-all duration-300 ${
                  stockTone === "soldOut"
                    ? "bg-red-500"
                    : stockTone === "low"
                      ? "bg-amber-500"
                      : "bg-emerald-500"
                }`}
                style={{ width: `${pctRemaining}%` }}
              />
            </div>
          )}
          {stockTone === "soldOut" && isActive && (
            <p className="text-xs font-medium text-red-400 mt-2">Sold out — choose another plan or wait for restock.</p>
          )}
          {stockTone === "low" && isActive && remN > 0n && (
            <p className="text-xs font-medium text-amber-400 mt-2">Low remaining inventory.</p>
          )}
        </>
      )}
    </div>
  );
}

export default function MyNodesV3Page() {
  const { address: userAccount } = useAccount();
  const [isMounted, setIsMounted] = useState(false);

  // Get all LRSNode data from global provider
  const {
    userReferCode,
    walletBalance,
    nodes,
    referNodes,
    isLoadingNodes,
    isLoadingReferNodes,
    refreshNodes,
  } = useLRSNodeV3();

  const { handleClaimNodeReward, handleClaimReferRewards, handleMigrateFromNode2 } = useNodeActionsV3();
  const [showBuyModal, setShowBuyModal] = useState(false);
  const [renewNodeKey, setRenewNodeKey] = useState<string | undefined>(undefined);
  const [showCreateReferModal, setShowCreateReferModal] = useState(false);
  const [claimingNodeKey, setClaimingNodeKey] = useState<string | null>(null);
  const [currentTxHash, setCurrentTxHash] = useState<`0x${string}` | undefined>(undefined);
  const [isMigrating, setIsMigrating] = useState(false);

  const v3AddressConfigured =
    !!LRSNODE_V3_CONTRACT_CONFIG.address && LRSNODE_V3_CONTRACT_CONFIG.address.length > 2;

  const {
    data: migrationStatsRaw,
    isLoading: isLoadingMigrationStats,
    refetch: refetchMigrationStats,
  } = useReadContract({
    ...LRSNODE_V3_CONTRACT_CONFIG,
    functionName: 'getNode2MigrationStats',
    args: userAccount ? [userAccount] : undefined,
    query: {
      enabled: !!userAccount && v3AddressConfigured,
    },
  });

  const migrationStats = migrationStatsRaw as Node2MigrationStats | undefined;

  /** Same readiness as on-chain `migrateFromNode2` except plan stock (`hasEnoughSlots`). Stock is still enforced by the contract. */
  const canMigrateExcludingStock = useMemo(() => {
    if (!migrationStats) return false;
    return (
      migrationStats.node2MigratableNodes > 0n &&
      migrationStats.pendingRewardBlockedNodes === 0n &&
      migrationStats.unclaimedLockRewards === 0n &&
      migrationStats.unclaimedReferRewards === 0n &&
      migrationStats.alreadyMigratedNodes === 0n
    );
  }, [migrationStats]);

  // Prevent hydration mismatch
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Transaction receipt hook
  const { isSuccess: isTxSuccess } = useWaitForTransactionReceipt({
    hash: currentTxHash,
    query: {
      enabled: !!currentTxHash,
    },
  });

  const refreshEverything = useCallback(() => {
    refreshNodes();
  }, [refreshNodes]);

  // Track transaction success
  useEffect(() => {
    if (isTxSuccess && currentTxHash) {
      refreshNodes();
      setCurrentTxHash(undefined);
      setClaimingNodeKey(null);
      setIsMigrating(false);
      void refetchMigrationStats();
      refreshEverything();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTxSuccess, currentTxHash]);

  useEffect(() => {
    if (userReferCode && userReferCode.length > 0) {
      setShowCreateReferModal(false);
      refreshEverything();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userReferCode]);

  // Format functions
  const formatAddress = (address: string) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const formatBalance = (balance?: bigint) => {
    if (!balance) return '0';
    return (Number(balance) / 1e18).toFixed(4);
  };

  const formatDate = (timestamp: bigint) => {
    return new Date(Number(timestamp) * 1000).toLocaleString();
  };

  const isNodeExpired = (expireTime: bigint) => {
    const now = Date.now();
    const currentTime = BigInt(Math.floor(now / 1000));
    return currentTime >= expireTime;
  };

  const getExpirationStatusColor = (expireTime: bigint) => {
    const now = Date.now();
    const currentTime = BigInt(Math.floor(now / 1000));
    return currentTime < expireTime ? 'text-green-400' : 'text-red-400';
  };

  const formatNodeKey = (nodeKey: string) => {
    if (nodeKey.length <= 16) return nodeKey;
    return `${nodeKey.slice(0, 8)}...${nodeKey.slice(-8)}`;
  };

  const copyToClipboard = (text: string) => {
    if (typeof window !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(text)
        .then(() => {
          alert('Copied to clipboard!');
        })
        .catch(err => {
          console.error('Failed to copy: ', err);
        });
    } else {
      // Fallback for older browsers or SSR
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        alert('Copied to clipboard!');
      } catch (err) {
        console.error('Failed to copy: ', err);
      }
      document.body.removeChild(textArea);
    }
  };

  const handleBuySuccess = useCallback(async () => {
    await refreshNodes();
    setShowBuyModal(false);
    refreshEverything();
  }, [refreshNodes, refreshEverything]);

  const handleBuyModalClose = useCallback(() => {
    setShowBuyModal(false);
    setRenewNodeKey(undefined);
    refreshEverything();
  }, [refreshEverything]);

  const onClaimReward = async (nodeKey: string) => {
    try {
      setClaimingNodeKey(nodeKey);
      const tx = await handleClaimNodeReward(nodeKey);
      setCurrentTxHash(tx.hash);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to claim node rewards';
      alert(errorMessage);
      setClaimingNodeKey(null);
    }
    refreshEverything();
  };

  const onRenewNode = async (nodeKey: string) => {
    try {
      setRenewNodeKey(nodeKey);
      setShowBuyModal(true);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to renew node';
      alert(errorMessage);
    }
    refreshEverything();
  };

  const handleClaimReferReward = async (nodeKey: string) => {
    try {
      const tx = await handleClaimReferRewards(nodeKey, userReferCode ?? '');
      setCurrentTxHash(tx.hash);
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Failed to claim refer reward');
    }
    refreshEverything();
  };


  const onMigrateFromNode2 = async () => {
    if (!userAccount) {
      alert('Connect your wallet to migrate.');
      return;
    }
    try {
      setIsMigrating(true);
      const tx = await handleMigrateFromNode2();
      setCurrentTxHash(tx.hash);
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Migration failed');
      setIsMigrating(false);
    }
    refreshEverything();
  };

  const handleCreateReferSuccess = useCallback(async () => {
    try {
      refreshEverything();
      if (userReferCode) {
        alert('Refer code created successfully!');
        setShowCreateReferModal(false);
      }
    } catch (error) {
      console.error('Failed to refresh refer code:', error);
      alert('Failed to refresh refer code');
    }
    refreshEverything();
  }, [userReferCode, refreshEverything]);

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <div className="px-4 md:px-6 lg:px-8 @container">
          <div className="w-full max-w-6xl mx-auto">
            <header className="flex flex-wrap gap-3 min-h-20 py-4 shrink-0 items-center transition-all ease-linear border-b">
              <div className="flex flex-1 items-center gap-2">
                <SidebarTrigger className="-ms-1" />
                <div className="max-lg:hidden lg:contents">
                  <Separator
                    orientation="vertical"
                    className="me-2 data-[orientation=vertical]:h-4"
                  />
                  <Breadcrumb>
                    <BreadcrumbList>
                      <BreadcrumbItem className="hidden md:block">
                        <BreadcrumbLink asChild>
                          <Link to="/">Home</Link>
                        </BreadcrumbLink>
                      </BreadcrumbItem>
                      <BreadcrumbSeparator className="hidden md:block" />
                      <BreadcrumbItem>
                        <BreadcrumbLink asChild>
                          <Link to="/lrsnode">LRSNode</Link>
                        </BreadcrumbLink>
                      </BreadcrumbItem>
                      <BreadcrumbSeparator className="hidden md:block" />
                      <BreadcrumbItem>
                        <BreadcrumbPage>My Nodes V3</BreadcrumbPage>
                      </BreadcrumbItem>
                    </BreadcrumbList>
                  </Breadcrumb>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  onClick={() => setShowBuyModal(true)}
                  className="bg-[#FF8A3C] text-white hover:bg-[#FF8A3C]/90"
                >
                  <RiAddLine className="size-5 mr-2" />
                  <span>Buy Node</span>
                </Button>
                <ConnectButton />
              </div>
            </header>

            <div className="py-6">
              <div className="flex items-center mb-6">
                <div className="w-1 h-8 bg-[#FF8A3C] mr-3"></div>
                <h1 className="text-3xl font-bold">My Nodes V3</h1>
              </div>

              <div className="w-full max-w-xs h-1 bg-gradient-to-r from-[#FF8A3C] to-transparent mb-8 rounded-full"></div>

              <Card className="bg-sidebar border border-border mb-8">
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                      <div className="text-sm text-muted-foreground mb-1">Wallet</div>
                      <div className="font-medium flex items-center">
                        <RiWallet3Line className="size-4 mr-2 text-[#FF8A3C]" />
                        <span className="text-sm md:text-base">
                          {!isMounted ? 'Loading...' : userAccount ? formatAddress(userAccount) : 'Not connected'}
                        </span>
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground mb-1">Balance</div>
                      <div className="font-medium text-[#FF8A3C]">
                        {!isMounted ? '0.0000 LRS' : formatBalance(walletBalance)} LRS
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {v3AddressConfigured && (
                <Card className="bg-sidebar border border-border mb-8 overflow-hidden backdrop-blur-sm">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <RiStackLine className="size-5 text-[#FF8A3C]" aria-hidden />
                      Plan inventory
                    </CardTitle>
                    <p className="text-sm text-muted-foreground font-normal">
                      Remaining keys per plan (on-chain). Highlights update when you refresh or after a purchase.
                    </p>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {V3_PLAN_STOCK_PLANS.map((p) => (
                        <V3PlanStockTile
                          key={p.id}
                          planId={p.id}
                          label={p.label}
                          enabled={v3AddressConfigured}
                        />
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {userAccount && v3AddressConfigured && (
                <Card className="bg-sidebar border border-border mb-8 overflow-hidden">
                  <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0 pb-2">
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <RiArrowRightUpLine className="size-5 text-[#FF8A3C]" />
                        Node 2 → Node 3 migration
                      </CardTitle>
                      <p className="text-sm text-muted-foreground mt-1">
                        Only active (unexpired) Node 2 keys migrate; expired keys stay on Node 2. Clear pending, lock, and
                        referral rewards on Node 2 first.
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="shrink-0"
                      onClick={() => void refetchMigrationStats()}
                      disabled={isLoadingMigrationStats}
                      aria-label="Refresh migration status"
                    >
                      <RiRefreshLine className={`size-4 ${isLoadingMigrationStats ? 'animate-spin' : ''}`} />
                    </Button>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {isLoadingMigrationStats ? (
                      <div className="flex justify-center py-6">
                        <RiLoader4Line className="size-8 animate-spin text-[#FF8A3C]" />
                      </div>
                    ) : migrationStats ? (
                      <>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                          <div className="rounded-lg border border-border bg-background/40 p-3">
                            <div className="text-muted-foreground text-xs mb-1">Node 2 total</div>
                            <div className="font-semibold tabular-nums">{migrationStats.node2TotalNodes.toString()}</div>
                          </div>
                          <div className="rounded-lg border border-border bg-background/40 p-3">
                            <div className="text-muted-foreground text-xs mb-1">Active (can migrate)</div>
                            <div className="font-semibold tabular-nums text-green-500">
                              {migrationStats.node2MigratableNodes.toString()}
                            </div>
                          </div>
                          <div className="rounded-lg border border-border bg-background/40 p-3">
                            <div className="text-muted-foreground text-xs mb-1">Expired (skipped)</div>
                            <div className="font-semibold tabular-nums text-muted-foreground">
                              {migrationStats.node2ExpiredNodes.toString()}
                            </div>
                          </div>
                          <div className="rounded-lg border border-border bg-background/40 p-3">
                            <div className="text-muted-foreground text-xs mb-1">Already migrated</div>
                            <div className="font-semibold tabular-nums">{migrationStats.alreadyMigratedNodes.toString()}</div>
                          </div>
                          <div className="rounded-lg border border-border bg-background/40 p-3">
                            <div className="text-muted-foreground text-xs mb-1">Blocked (pending rewards)</div>
                            <div className="font-semibold tabular-nums text-amber-500">
                              {migrationStats.pendingRewardBlockedNodes.toString()}
                            </div>
                          </div>
                          <div className="rounded-lg border border-border bg-background/40 p-3">
                            <div className="text-muted-foreground text-xs mb-1">Unclaimed lock rows</div>
                            <div className="font-semibold tabular-nums text-amber-500">
                              {migrationStats.unclaimedLockRewards.toString()}
                            </div>
                          </div>
                          <div className="rounded-lg border border-border bg-background/40 p-3">
                            <div className="text-muted-foreground text-xs mb-1">Unclaimed refer rows</div>
                            <div className="font-semibold tabular-nums text-amber-500">
                              {migrationStats.unclaimedReferRewards.toString()}
                            </div>
                          </div>
                        </div>

                        {migrationStats.node2TotalNodes === BigInt(0) ? (
                          <p className="text-sm text-muted-foreground">
                            No Node 2 nodes are associated with this wallet on the legacy contract.
                          </p>
                        ) : migrationStats.node2MigratableNodes === BigInt(0) ? (
                          <p className="text-sm text-muted-foreground">
                            All Node 2 nodes for this wallet are expired. Renew or replace them on Node 2 if applicable;
                            migration only moves active (unexpired) keys.
                          </p>
                        ) : (
                          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                            <Button
                              type="button"
                              className="bg-[#FF8A3C] text-white hover:bg-[#FF8A3C]/90 w-full sm:w-auto"
                              disabled={!canMigrateExcludingStock || isMigrating}
                              onClick={() => void onMigrateFromNode2()}
                            >
                              {isMigrating ? (
                                <RiLoader4Line className="size-4 animate-spin mr-2" />
                              ) : (
                                <RiArrowRightUpLine className="size-4 mr-2" />
                              )}
                              Migrate from Node 2
                            </Button>
                            {migrationStats.canMigrate ? (
                              <span className="text-xs text-green-500">Ready to migrate on-chain.</span>
                            ) : canMigrateExcludingStock && !migrationStats.hasEnoughSlots ? (
                              <span className="text-xs text-amber-500/90">
                                Node 2 blockers are clear. If migration reverts, raise V3 plan stock for the migration
                                plan or use Plan inventory above — the contract still enforces capacity.
                              </span>
                            ) : (
                              <span className="text-xs text-muted-foreground">
                                Clear blockers on Node 2 (claim rewards, locks, referrals) and finish migrating all active
                                keys in one batch before retrying.
                              </span>
                            )}
                          </div>
                        )}
                      </>
                    ) : (
                      <p className="text-sm text-muted-foreground">Could not load migration status.</p>
                    )}
                  </CardContent>
                </Card>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <Card className="bg-sidebar border border-border">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm text-muted-foreground">Your Nodes</div>
                      <RiNodeTree className="size-5 text-[#FF8A3C]" />
                    </div>
                    <div className="text-3xl font-bold">{nodes.length}</div>
                  </CardContent>
                </Card>

                <Card className="bg-sidebar border border-border">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm text-muted-foreground">Referral Nodes</div>
                      <RiShareLine className="size-5 text-[#FF8A3C]" />
                    </div>
                    <div className="text-3xl font-bold">{referNodes.length}</div>
                  </CardContent>
                </Card>
              </div>

              <Tabs defaultValue="nodes" className="mb-8">
                <TabsList className="mb-6">
                  <TabsTrigger value="nodes">List of Nodes</TabsTrigger>
                  <TabsTrigger value="referrals">Your Refer Node List</TabsTrigger>
                </TabsList>

                <TabsContent value="nodes">
                  <Card className="bg-sidebar border border-border">
                    <CardHeader>
                      <CardTitle>Your Nodes</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {isLoadingNodes ? (
                        <div className="flex justify-center py-8">
                          <RiLoader4Line className="size-8 animate-spin text-[#FF8A3C]" />
                        </div>
                      ) : nodes.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                          <RiNodeTree size={48} className="mb-4" />
                          <p>No nodes found. Purchase a node to get started.</p>
                        </div>
                      ) : (
                        <div className="overflow-x-auto">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Node Key ID</TableHead>
                                <TableHead>Node Key</TableHead>
                                <TableHead>Plan</TableHead>
                                <TableHead>Purchased on</TableHead>
                                <TableHead>Expire on</TableHead>
                                <TableHead>Rewards</TableHead>
                                <TableHead>Actions</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {nodes.map((node) => (
                                <TableRow key={node.nodeKeyID.toString()}>
                                  <TableCell>{node.nodeKeyID.toString()}</TableCell>
                                  <TableCell>
                                    <div className="flex items-center space-x-2">
                                      <span className="font-mono text-xs">{formatAddress(node.nodeKey)}</span>
                                      <button
                                        onClick={() => copyToClipboard(node.nodeKey)}
                                        className="text-[#FF8A3C] hover:text-[#FF8A3C]/80"
                                      >
                                        <RiFileCopyLine size={16} />
                                      </button>
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <span className="font-medium">
                                      {PLAN_TABLE_LABEL[node.planId.toString()] ?? `Plan ${node.planId.toString()}`}
                                    </span>
                                  </TableCell>
                                  <TableCell className="text-xs">{formatDate(node.buyTime)}</TableCell>
                                  <TableCell className="text-xs">
                                    <span className={getExpirationStatusColor(node.expireTime)}>
                                      {formatDate(node.expireTime)}
                                    </span>
                                  </TableCell>
                                  <TableCell>
                                    {node.pendingReward ? formatBalance(node.pendingReward) : '0'} LRS
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex items-center gap-2">
                                      <Button
                                        size="sm"
                                        onClick={() => {
                                          if(node.pendingReward && node.pendingReward > 0) {
                                            onClaimReward(node.nodeKey);
                                          } else {
                                            alert('No rewards to claim');
                                          }
                                        }}
                                        disabled={claimingNodeKey === node.nodeKey}
                                        className="bg-[#FF8A3C] hover:bg-[#FF8A3C]/90"
                                      >
                                        {claimingNodeKey === node.nodeKey ? (
                                          <RiLoader4Line className="size-4 animate-spin" />
                                        ) : (
                                          'Claim'
                                        )}
                                      </Button>
                                      {isNodeExpired(node.expireTime) && (
                                        <Button
                                          size="sm"
                                          onClick={() => onRenewNode(node.nodeKey)}
                                          variant="outline"
                                          className="border-red-500 text-red-500 hover:bg-red-500/10"
                                        >
                                          Renew
                                        </Button>
                                      )}
                                    </div>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="referrals">
                  <Card className="bg-sidebar border border-border mb-6">
                    <CardHeader>
                      <CardTitle>Your Refer Node List</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {isLoadingReferNodes ? (
                        <div className="flex justify-center py-8">
                          <RiLoader4Line className="size-8 animate-spin text-[#FF8A3C]" />
                        </div>
                      ) : referNodes.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                          <RiShareLine size={48} className="mb-4" />
                          <p>No referral nodes found. Share your referral code to earn rewards.</p>
                        </div>
                      ) : (
                        <div className="overflow-x-auto">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Node Key</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {referNodes.map((node) => (
                                <TableRow key={node.nodeKey}>
                                  <TableCell>
                                    <div className="flex items-center space-x-2">
                                      <span className="font-mono text-xs">{formatNodeKey(node.nodeKey)}</span>
                                      <button
                                        onClick={() => copyToClipboard(node.nodeKey)}
                                        className="text-[#FF8A3C] hover:text-[#FF8A3C]/80"
                                      >
                                        <RiFileCopyLine size={16} />
                                      </button>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  <Card className="bg-sidebar border border-border">
                    <CardHeader>
                      <CardTitle>Create Refer Code</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {userReferCode && userReferCode.length > 0 ? (
                        <>
                          <div className="flex flex-col md:flex-row gap-4">
                            <div className="flex-grow">
                              <div className="text-sm text-muted-foreground mb-2">Your Refer Code</div>
                              <div className="flex">
                                <Input
                                  value={userReferCode}
                                  readOnly
                                  className="bg-background font-mono"
                                />
                                <Button
                                  variant="outline"
                                  className="ml-2 hover:border-[#FF8A3C] hover:text-[#FF8A3C]"
                                  onClick={() => copyToClipboard(userReferCode ?? '')}
                                >
                                  <RiFileCopyLine className="size-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                          <p className="text-sm text-muted-foreground mt-4">
                            Share your refer code with others to earn rewards when they buy nodes.
                          </p>
                        </>
                      ) : (
                        <>
                          <div className="flex justify-center mb-4">
                            <Button
                              onClick={() => setShowCreateReferModal(true)}
                              className="bg-[#FF8A3C] hover:bg-[#FF8A3C]/90"
                            >
                              <RiShareLine className="size-4 mr-2" />
                              Create Refer Code
                            </Button>
                          </div>
                          <p className="text-sm text-muted-foreground text-center">
                            Create a unique refer code to start earning rewards from referrals.
                          </p>
                        </>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </div>
      </SidebarInset>

      <BuyNodeModal
        isOpen={showBuyModal}
        nodeKey={renewNodeKey}
        onClose={handleBuyModalClose}
        onSuccess={handleBuySuccess}
        useV3={true}
      />

      <CreateReferCodeModal
        isOpen={showCreateReferModal}
        onClose={() => setShowCreateReferModal(false)}
        onSuccess={handleCreateReferSuccess}
        useV3={true}
      />
    </SidebarProvider>
  );
}



